/**
 * custom-stemregenHorizon: Out of Stock Notification
 *
 * The block is rendered by Liquid only when the resolved variant is sold out
 * (inventory_policy 'deny', quantity <= 0), so nothing enters the DOM — and Klaviyo
 * never builds a form — while the product is in stock.
 *
 * Because the element comes and goes, variant switching is handled the way the rest
 * of the theme handles it: the variant picker fetches the full page for the newly
 * selected variant and hands it over on `variant:update` as `event.detail.data.html`
 * (see variant-picker.js:220, product-form.js:450, product-price.js:43). We take the
 * block straight out of that document rather than re-deriving stock state in JS.
 */

import { Component } from '@theme/component';

/* ------------------------------------------------------------------
   Preview mode — QA only. Branch: issue/sport-oos-messaging.
   Renders the sold-out presentation on an in-stock PDP so it can be reviewed
   without touching inventory or inventory policy.

     ?stem_preview=oos&stem_preview_form=WB3ci7

   The form ID has to be supplied because the block no longer renders while in
   stock, so there is nothing on the page to read it from.

   Optional: &stem_preview_heading=  &stem_preview_sub=  (empty value blanks it)

   Presentation only. Nothing here touches the cart, inventory, or any metafield.
   ------------------------------------------------------------------ */

const OOSPreview = (() => {
  const state = { active: false, formId: '', heading: '', subheading: '' };

  if (typeof window === 'undefined') return state;

  const params = new URLSearchParams(window.location.search);

  if (params.get('stem_preview') !== 'oos') return state;
  if (!window.location.pathname.includes('/products/')) return state;

  // Lands in a class name, so validate rather than trust the query string
  const formId = params.get('stem_preview_form') || '';

  state.active = true;
  state.formId = /^[A-Za-z0-9_-]{1,32}$/.test(formId) ? formId : '';
  state.heading = params.get('stem_preview_heading') ?? 'Out of Stock';
  state.subheading = params.get('stem_preview_sub') || '';

  return state;
})();

/**
 * Builds a block matching what the Liquid renders, for preview only.
 * @returns {HTMLElement}
 */
function buildPreviewNotification() {
  const block = document.createElement('oos-notification');
  block.className = 'custom-stemregenhorizon-oos-notification';
  block.setAttribute('data-form-id', OOSPreview.formId);
  block.setAttribute('data-stem-preview', '');

  if (OOSPreview.heading) {
    const heading = document.createElement('h3');
    heading.className = 'oos-notification-heading';
    heading.textContent = OOSPreview.heading;
    block.appendChild(heading);
  }

  if (OOSPreview.subheading) {
    const subheading = document.createElement('p');
    subheading.className = 'oos-notification-subheading';
    subheading.textContent = OOSPreview.subheading;
    block.appendChild(subheading);
  }

  const formContainer = document.createElement('div');
  formContainer.className = `klaviyo-form-${OOSPreview.formId}`;
  block.appendChild(formContainer);

  return block;
}

/**
 * Applies the rest of the sold-out presentation, which Liquid would otherwise handle:
 * disabled Add to Cart, and both checkout buttons hidden.
 */
function applyPreviewSoldOutState() {
  document.querySelectorAll('.buy-buttons-block button[name="add"]').forEach((button) => {
    button.disabled = true;
    // Real sold-out markup omits the cart icon
    button.querySelector('.add-to-cart-icon')?.remove();
    const content = button.querySelector('.add-to-cart-text__content');
    if (content) content.textContent = 'Sold out';
  });

  // accelerated-checkout.liquid:27 takes `hidden`; the custom button does not render at all
  document.querySelectorAll('.accelerated-checkout-block').forEach((el) => el.setAttribute('hidden', ''));
  document.querySelectorAll('.custom-checkout-button').forEach((el) => el.remove());

  // custom-stemregenhorizon-in-stock.liquid flips to its out-of-stock state
  document.querySelectorAll('.stock-status.in-stock').forEach((pill) => {
    pill.classList.replace('in-stock', 'out-of-stock');
    pill
      .querySelector('.stock-icon path')
      ?.setAttribute('d', 'm9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z');
    const label = pill.querySelector('span');
    if (label) label.textContent = 'Out of Stock';
  });
}

function applyPreview() {
  if (!OOSPreview.active) return;

  // A crawled preview URL must not index as a sold-out page
  if (!document.querySelector('meta[data-stem-preview]')) {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    meta.setAttribute('data-stem-preview', '');
    document.head.appendChild(meta);
  }

  applyPreviewSoldOutState();

  const anchor = document.querySelector('.buy-buttons-block');

  if (!OOSPreview.formId) {
    console.warn('[stem-preview] Add &stem_preview_form=WB3ci7 to preview the Klaviyo form.');
    return;
  }

  if (anchor && !document.querySelector('oos-notification')) {
    anchor.after(buildPreviewNotification());
  }
}

/**
 * Keeps the block in sync with the selected variant.
 *
 * The element is absent while in stock, so this cannot live on the component — there
 * would be nothing listening. It runs at module level and owns insert, replace, and
 * removal against the buy-buttons anchor the Liquid renders it after.
 *
 * @param {CustomEvent} event - The theme's variant:update event
 */
function syncFromVariantUpdate(event) {
  if (OOSPreview.active) return;

  const source = event.detail?.data?.html;
  if (!source) return;

  const current = document.querySelector('oos-notification');
  const next = source.querySelector('oos-notification');

  // Newly in stock — Liquid dropped the block, so we drop ours
  if (current && !next) {
    current.remove();
    return;
  }

  if (!next) return;

  // Replacing rather than mutating gives us a fresh connectedCallback, which is what
  // re-runs the Klaviyo init that the Quick Add fixes in 08a8a97 / 74d130c depend on.
  if (current) {
    if (current.getAttribute('data-variant-id') === next.getAttribute('data-variant-id')) return;
    current.replaceWith(next);
    return;
  }

  document.querySelector('.buy-buttons-block')?.after(next);
}

export class OOSNotificationComponent extends Component {
  /**
   * Initializes the component. Reaching here at all means Liquid decided this
   * variant is sold out, so there is no stock check to repeat.
   */
  connectedCallback() {
    super.connectedCallback();

    this.productId = this.getAttribute('data-product-id');
    this.currentVariantId = this.getAttribute('data-variant-id');

    this.initializeKlaviyoForm();
    this.setupModalListener();
  }

  /**
   * Sets up listener for modal/dialog open events
   */
  setupModalListener() {
    const dialog = this.closest('dialog, [role="dialog"], quick-add-dialog');

    if (dialog) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
            if (dialog.hasAttribute('open')) {
              setTimeout(() => this.initializeKlaviyoForm(), 200);
            }
          }
        });
      });

      observer.observe(dialog, { attributes: true });
      this._modalObserver = observer;
    }
  }

  /**
   * Cleanup when component is removed
   */
  disconnectedCallback() {
    super.disconnectedCallback();

    if (this._modalObserver) {
      this._modalObserver.disconnect();
    }
  }

  /**
   * Reinitializes Klaviyo forms
   * Called when component connects (modal opens) to ensure forms load
   */
  initializeKlaviyoForm() {
    const formContainer = this.querySelector('[class*="klaviyo-form-"]');

    if (!formContainer) {
      return;
    }

    const formClass = Array.from(formContainer.classList).find((cls) => cls.startsWith('klaviyo-form-'));

    if (!formClass) {
      return;
    }

    // Check if form is already loaded (has children and any Klaviyo version class)
    const hasKlaviyoVersionClass = Array.from(formContainer.classList).some((cls) =>
      cls.startsWith('form-version-cid-')
    );

    if (formContainer.children.length > 0 && hasKlaviyoVersionClass) {
      if (!this._cachedFormHTML) {
        this._cachedFormHTML = formContainer.cloneNode(true);
      }
      return;
    }

    // If form is empty but we have cached HTML, restore it
    if (formContainer.children.length === 0 && this._cachedFormHTML) {
      const newForm = this._cachedFormHTML.cloneNode(true);
      formContainer.replaceWith(newForm);
      return;
    }

    // Force Klaviyo to reinitialize by removing and re-adding
    const parentElement = formContainer.parentElement;
    const formHTML = formContainer.outerHTML;

    formContainer.remove();

    setTimeout(() => {
      parentElement.insertAdjacentHTML('beforeend', formHTML);

      setTimeout(() => {
        if (typeof window._learnq !== 'undefined') {
          window._learnq.push(['initForms']);
          window._learnq.push(['identify', {}]);
        }
      }, 100);
    }, 50);
  }
}

// Register the custom element
if (!customElements.get('oos-notification')) {
  customElements.define('oos-notification', OOSNotificationComponent);
}

document.addEventListener('variant:update', syncFromVariantUpdate);

if (OOSPreview.active) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyPreview, { once: true });
  } else {
    applyPreview();
  }
  // product-form.js swaps the ATC button in from the fetched HTML, which would undo the preview
  document.addEventListener('variant:update', () => setTimeout(applyPreview, 100));
  document.addEventListener('sticky-buy-button:shown', () => setTimeout(applyPreviewSoldOutState, 50));
}
